import { useEffect, useState } from "react";
import { useToast } from "../../../hooks/use-toast";
import { Operation, OpsTemplate, UploadConfig } from "../types";
import {
  useCreateOperation,
  useUpdateOperation,
} from "./use-operations-queries";

interface FormData {
  ops: string;
  db_name: string;
  screens: string[];
}

interface UploadFormData {
  key: string;
  title: string;
  doc_type: string;
  extension_file: string;
  upload_title: string;
}

interface UseOperationFormProps {
  operation: Operation | null;
  isOpen: boolean;
  onClose: () => void;
}

export const useOperationForm = ({
  operation,
  isOpen,
  onClose,
}: UseOperationFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    ops: "",
    db_name: "",
    screens: [],
  });
  const [uploads, setUploads] = useState<UploadFormData[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {}
  );
  const [uploadErrors, setUploadErrors] = useState<Record<number, string>>({});
  const [apiError, setApiError] = useState<string>("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  const { toast } = useToast();
  const createMutation = useCreateOperation();
  const updateMutation = useUpdateOperation();

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        ops: "",
        db_name: "",
        screens: [],
      });
      setUploads([]);
      setErrors({});
      setUploadErrors({});
      setApiError("");
      setSelectedTemplateId(null);

      return;
    }

    if (operation) {
      setFormData({
        ops: operation.ops,
        db_name: operation.db_name,
        screens: operation.screens,
      });
      setUploads(
        operation.uploads.map((upload) => ({
          key: upload.key,
          title: upload.title,
          doc_type: upload.doc_type,
          extension_file: upload.extension_file,
          upload_title: upload.upload_title,
        }))
      );
      setSelectedTemplateId(operation.template_id ?? null);
      setErrors({});
      setUploadErrors({});
      setApiError("");
      return;
    }
  }, [operation, isOpen]);

  const validateUploads = (): boolean => {
    const newUploadErrors: Record<number, string> = {};

    uploads.forEach((upload, index) => {
      if (!upload.key.trim()) {
        newUploadErrors[index] = "Key é obrigatória";
        return;
      }

      if (!upload.title.trim()) {
        newUploadErrors[index] = "Title é obrigatório";
        return;
      }

      if (!upload.doc_type.trim()) {
        newUploadErrors[index] = "Doc type é obrigatório";
        return;
      }

      if (!upload.extension_file.trim()) {
        newUploadErrors[index] = "Extension file é obrigatória";
        return;
      }

      if (!upload.upload_title.trim()) {
        newUploadErrors[index] = "Upload title é obrigatório";
        return;
      }
    });

    setUploadErrors(newUploadErrors);
    return Object.keys(newUploadErrors).length === 0;
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.ops.trim()) {
      newErrors.ops = "Operação é obrigatória";
    } else if (!/^[a-z0-9-]+$/.test(formData.ops)) {
      newErrors.ops =
        "Operação deve conter apenas letras minúsculas, números e hífens";
    }

    if (!formData.db_name) {
      newErrors.db_name = "Banco de dados é obrigatório";
    }

    setErrors(newErrors);
    const formValid = Object.keys(newErrors).length === 0;
    const uploadsValid = validateUploads();

    return formValid && uploadsValid;
  };

  const addUpload = () => {
    setUploads([
      ...uploads,
      {
        key: "",
        title: "",
        doc_type: "",
        extension_file: "",
        upload_title: "",
      },
    ]);
  };

  const removeUpload = (index: number) => {
    setUploads(uploads.filter((_, i) => i !== index));
    const newUploadErrors = { ...uploadErrors };
    delete newUploadErrors[index];
    setUploadErrors(newUploadErrors);
  };

  const updateUpload = (
    index: number,
    field: keyof UploadFormData,
    value: string
  ) => {
    const newUploads = [...uploads];
    newUploads[index] = { ...newUploads[index], [field]: value };
    setUploads(newUploads);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    if (!validate()) {
      return;
    }

    const uploadsArray: UploadConfig[] = uploads.map((upload) => ({
      key: upload.key.trim(),
      title: upload.title.trim(),
      doc_type: upload.doc_type.trim(),
      extension_file: upload.extension_file.trim(),
      upload_title: upload.upload_title.trim(),
    }));

    const requestData = {
      ops: formData.ops.trim(),
      db_name: formData.db_name,
      screens: formData.screens.length > 0 ? formData.screens : undefined,
      uploads: uploadsArray.length > 0 ? uploadsArray : undefined,
      template_id: selectedTemplateId,
    };

    try {
      if (operation) {
        await updateMutation.mutateAsync(requestData);
        toast({
          title: "Sucesso",
          description: "Operação atualizada com sucesso",
        });
        return;
      }
      await createMutation.mutateAsync(requestData);
      toast({
        title: "Sucesso",
        description: "Operação criada com sucesso",
      });

      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao salvar operação";
      setApiError(errorMessage);
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const applyTemplate = (template: OpsTemplate) => {
    setSelectedTemplateId(template.id);
    setFormData((prev) => ({
      ...prev,
      screens: template.screens,
    }));
    setUploads(
      template.uploads.map((u) => ({
        key: u.key,
        title: u.title,
        doc_type: u.doc_type,
        extension_file: u.extension_file,
        upload_title: u.upload_title,
      }))
    );
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return {
    formData,
    setFormData,
    uploads,
    errors,
    uploadErrors,
    apiError,
    isLoading,
    addUpload,
    removeUpload,
    updateUpload,
    handleSubmit,
    selectedTemplateId,
    applyTemplate,
  };
};
